import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db, isDevelopmentMode } from '../firebase';
import { EmailService } from '../utils/emailService';
import { AnalyticsService } from '../utils/analyticsService';
import type { CampRegistration } from '../types';

import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react';

const Registration: React.FC = () => {
  const [formData, setFormData] = useState<Omit<CampRegistration, 'id' | 'registrationDate'>>({
    name: '',
    age: 0,
    email: '',
    phone: '',
    parentName: '',
    parentEmail: '',
    city: '',
    allergies: '',
    additionalInfo: '',
    agreement: false,
  });
  const [registrationCount, setRegistrationCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnder7Message, setShowUnder7Message] = useState(false);
  const [showUnder18Section, setShowUnder18Section] = useState(false);
  const [hasStartedForm, setHasStartedForm] = useState(false);

  const maxCapacity = 50;

  useEffect(() => {
    fetchRegistrationCount();
  }, []);

  // Handle blur event to show sections when user clicks away from age field
  const handleAgeBlur = () => {
    if (formData.age > 0 && formData.age < 7) {
      setShowUnder7Message(true);
    } else {
      setShowUnder7Message(false);
    }

    if (formData.age > 0 && formData.age < 18) {
      setShowUnder18Section(true);
    } else {
      setShowUnder18Section(false);
    }
  };

  const fetchRegistrationCount = async () => {
    try {
      // Check if Firebase is properly configured
      if (!db) {
        setRegistrationCount(Math.floor(Math.random() * 30) + 10); // Mock 10-40 registrations
        return;
      }

      const registrationsCollection = collection(db, 'registrations');
      const snapshot = await getDocs(registrationsCollection);
      setRegistrationCount(snapshot.size);
    } catch (err) {
      console.warn('Firebase error. Using mock data.', err);
      setRegistrationCount(Math.floor(Math.random() * 30) + 10); // Mock data
    }
  };



  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Vārds ir obligāts';
    }

    if (!formData.age || formData.age <= 0) {
      newErrors.age = 'Lūdzu ievadiet derīgu vecumu';
    }

    // Email is always required
    if (!formData.email.trim()) {
      newErrors.email = 'E-pasts ir obligāts';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Nepareizs e-pasta formāts';
    }

    // For under 18, parent name is required
    if (formData.age < 18 && formData.age > 0) {
      if (!formData.parentName?.trim()) {
        newErrors.parentName = 'Vecāka/aizbildņa vārds ir obligāts bērniem līdz 18 gadiem';
      }
    }

    if (!formData.agreement) {
      newErrors.agreement = 'Jāpiekrīt noteikumiem un nosacījumiem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Track form error
      AnalyticsService.trackFormInteraction('registration', 'error');
      return;
    }

    // Track form start
    AnalyticsService.trackFormInteraction('registration', 'start');

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {


      // Submit registration
      const registrationData: Omit<CampRegistration, 'id'> = {
        ...formData,
        registrationDate: new Date(),
      };

      // Submit registration to Firestore
      if (!db) {
        // In development mode, simulate successful registration
        console.log('Development mode: Registration data would be saved:', registrationData);
      } else {
        await addDoc(collection(db, 'registrations'), registrationData);
      }

      // Send welcome email
      try {
        const emailResult = await EmailService.sendWelcomeEmail(formData);
        if (!emailResult.success) {
          console.warn('Email sending failed:', emailResult.error);
          // Don't fail the registration if email fails
        }
      } catch (emailError) {
        console.warn('Email sending error:', emailError);
        // Don't fail the registration if email fails
      }

      setSubmitStatus('success');

      // Track successful registration completion
      AnalyticsService.trackFormInteraction('registration', 'complete');

      // Track Google Ads conversion for successful registration
      AnalyticsService.trackRegistrationConversion();

      setFormData({
        name: '',
        age: 0,
        email: '',
        phone: '',
        parentName: '',
        parentEmail: '',
        city: '',
        allergies: '',
        additionalInfo: '',
        agreement: false,
      });

      // Update registration count
      fetchRegistrationCount();

    } catch (error) {
      console.error('Error submitting registration:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    // Type assertion is safe here as we know it's an input element if type is checkbox
    const checked = (e.target as HTMLInputElement).checked;

    // Track when user first starts interacting with the form
    if (!hasStartedForm) {
      setHasStartedForm(true);
      AnalyticsService.trackEvent('registration_form_started', {
        first_field: name
      });
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle form focus - track when user starts engaging
  const handleFormFocus = () => {
    if (!hasStartedForm) {
      setHasStartedForm(true);
      AnalyticsService.trackEvent('registration_form_focused');
    }
  };

  return (
    <section id="registration" className="py-20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-primary-100 to-accent-200 mb-6">
            Pieteikšanās ‘SuperDiena’ Pasākumam
          </h2>
          <p className="text-lg text-gray-300 max-w-3xl mx-auto">
            Aicinām pieteikties ‘SuperDienai’, lai mēs varētu sagatavot dāvanas, uzkodas un vakariņas tā lai visiem pietiktu. Paldies par sapratni! :)
          </p>
        </motion.div>

        {/* Registration Progress */}
        <div className="max-w-2xl mx-auto mb-8">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-2xl font-bold text-white">
              Pieteikšanās Progress
            </h3>
            <div className="text-right">
              <span className="text-lg font-semibold">
                {registrationCount} / {maxCapacity}
              </span>
              <span className="text-sm text-gray-400 ml-2">
                vietas aizņemtas
              </span>
            </div>
          </div>
          <div className="w-full bg-white/10 rounded-full h-2.5">
            <div
              className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full h-2.5"
              style={{ width: `${Math.min((registrationCount / maxCapacity) * 100, 100)}%` }}
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-900/30 border border-green-500/30 rounded-xl p-6 mb-8"
            >
              <div className="flex items-center">
                <CheckCircle className="text-green-400 w-8 h-8 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-green-400 font-bold text-lg">Pieteikšanās veiksmīga!</h3>
                  <p className="text-green-200">
                    Paldies par pieteikšanos! Mēs nosūtījām jums e-pastu ar pasākuma programmu un svarīgu informāciju.
                  </p>
                </div>
              </div>
            </motion.div>
          )}



          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-900/30 border border-red-500/30 rounded-xl p-6 mb-8"
            >
              <div className="flex items-center">
                <XCircle className="text-red-400 w-8 h-8 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-red-400 font-bold text-lg">Pieteikšanās neizdevās</h3>
                  <p className="text-red-200">
                    Radās tehniska problēma. Lūdzu mēģiniet vēlāk vai sazinieties ar mums.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name and Age */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  Vārds *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  onFocus={handleFormFocus}
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500 ${errors.name ? 'border-red-500' : 'border-white/10'
                    }`}
                  placeholder="Ievadiet pilno vārdu"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  Vecums *
                </label>
                <input
                  type="number"
                  name="age"
                  value={formData.age || ''}
                  onChange={handleInputChange}
                  onBlur={handleAgeBlur}
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500 ${errors.age ? 'border-red-500' : 'border-white/10'
                    }`}
                  placeholder="Ievadiet vecumu"
                />
                {errors.age && <p className="text-red-500 text-sm mt-1">{errors.age}</p>}
                {showUnder7Message && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className="mt-2 p-3 bg-blue-900/30 border border-blue-500/30 rounded-lg"
                  >
                    <p className="text-blue-200 text-sm">
                      Bērniem līdz 7 gadu vecumam jāpiedalās kopā ar vecāku vai aizbildni.
                    </p>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  E-pasts *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500 ${errors.email ? 'border-red-500' : 'border-white/10'
                    }`}
                  placeholder="juris@example.com"
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  Telefons (neobligāti)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500"
                  placeholder="+371 12345678"
                />
              </div>
            </div>

            {/* City */}
            <div>
              <label className="block text-gray-300 font-medium mb-2">
                No kuras pilsētas? (neobligāti)
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500"
                placeholder="Viļāni, Varakļāni, Rēzekne..."
              />
            </div>

            {/* Parent/Guardian info (for under 18) */}
            {showUnder18Section && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="p-6 bg-blue-900/30 rounded-xl border border-blue-500/30"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-blue-300 mb-2">
                    Vecāka/aizbildņa informācija (obligāta bērniem līdz 18 gadiem)
                  </h3>
                </div>

                <div>
                  <label className="block text-gray-300 font-medium mb-2">
                    Vecāka/aizbildņa vārds *
                  </label>
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-white/5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500 ${errors.parentName ? 'border-red-500' : 'border-white/10'
                      }`}
                    placeholder="Vecāka pilnais vārds"
                  />
                  {errors.parentName && <p className="text-red-500 text-sm mt-1">{errors.parentName}</p>}
                </div>
              </motion.div>
            )}

            {/* Allergies and Additional Info */}
            <div className="space-y-6">
              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  Alerģijas vai īpašas prasības (neobligāti)
                </label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500"
                  placeholder="Lūdzu norādiet visas alerģijas vai īpašas prasības..."
                />
              </div>

              <div>
                <label className="block text-gray-300 font-medium mb-2">
                  Papildu informācija (neobligāti)
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-white placeholder-gray-500"
                  placeholder="Cita svarīga informācija, ko mums vajadzētu zināt..."
                />
              </div>
            </div>

            {/* Agreement */}
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                name="agreement"
                checked={formData.agreement}
                onChange={handleInputChange}
                className="mt-1 h-5 w-5 text-primary-600 border-white/10 rounded focus:ring-primary-500 bg-white/5"
              />
              <label className="text-gray-300">
                <span className="font-medium">
                  Piekrītu <a href="/noteikumi" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">noteikumiem un nosacījumiem</a> *
                </span>
                <br />
                <span className="text-sm text-gray-400">
                  Es apstiprinu, ka sniegtā informācija ir patiesa un piekrītu "SuperDiena" pasākuma <a href="/noteikumi" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 underline">noteikumiem un nosacījumiem</a>. Es saprotu, ka dalība aktivitātēs ir uz savu atbildību.
                </span>
              </label>
            </div>
            {errors.agreement && <p className="text-red-500 text-sm">{errors.agreement}</p>}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting || registrationCount >= maxCapacity}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${isSubmitting || registrationCount >= maxCapacity
                ? 'bg-gray-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 shadow-lg hover:shadow-xl'
                } text-white`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-2"></div>
                  Reģistrē...
                </div>
              ) : registrationCount >= maxCapacity ? (
                'Pieteikšanās slēgta – visas vietas aizņemtas.'
              ) : (
                'Pieteikties "SuperDiena"'
              )}
            </motion.button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Registration;
